import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../lib/firebase/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { checkPendingRemovals } from '../lib/firebase/friendService';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Function to refresh user profile data
  const refreshUserProfile = async () => {
    if (!currentUser) return null;
    
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        
        // Update the current user state with fresh data
        setCurrentUser(prev => ({
          ...prev,
          photoURL: userData.photoURL || prev.photoURL,
          profile: userData
        }));
        
        return userData;
      }
      return null;
    } catch (error) {
      console.error('Error refreshing user profile:', error);
      return null;
    }
  };
  
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Get or create user profile document
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        
        if (!userSnap.exists()) {
          // Create new user profile
          await setDoc(userRef, {
            displayName: user.displayName || 'Anonymous',
            email: user.email,
            photoURL: user.photoURL || null,
            joinDate: serverTimestamp(),
            postCount: 0,
            bio: '',
            skills: []
          });
        }
        
        // Get the latest user profile data
        const updatedUserSnap = await getDoc(userRef);
        const userData = updatedUserSnap.data();
        
        // Combine auth user with Firestore profile
        setCurrentUser({
          ...user,
          profile: userData
        });
        
        // Check for any pending friend removals that need to be processed
        try {
          console.log('Checking for pending friend removals at login...');
          const result = await checkPendingRemovals(user.uid);
          if (result && result.processed > 0) {
            console.log(`Processed ${result.processed} pending friend removals at login`);
            // Refresh user profile after processing removals
            setTimeout(refreshUserProfile, 1000);
          }
        } catch (error) {
          console.error('Error checking pending friend removals:', error);
        }
      } else {
        setCurrentUser(null);
      }
      
      setLoading(false);
    });
    
    return unsubscribe;
  }, []);
  
  const value = {
    user: currentUser,
    loading,
    refreshUserProfile
  };
  
  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 